import { useState, useEffect, useCallback } from 'react';
import { 
  FileText, Users, CheckCircle, MessageSquare, Star, HelpCircle, 
  TrendingUp, Eye, MousePointer, Download, Clock, UserPlus,
  ArrowUpRight, ArrowDownRight, RefreshCw, Calendar, BarChart3
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { analyticsAPI, statsAPI } from '../../lib/api';

const DashboardOverview = () => {
  const [stats, setStats] = useState({ guides: 0, services: 0, leads: 0, verified_services: 0, faqs: 0, ratings: 0 });
  const [analytics, setAnalytics] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30');

  // Format number to Bengali locale
  const formatNumber = (num) => (num ?? 0).toLocaleString('bn-BD');
  
  // Format time range with Bengali digits
  const formatTimeRange = (days) => {
    const bnDigits = { '7': '৭', '30': '৩০', '90': '৯০' };
    return bnDigits[days] || days;
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, analyticsRes, activityRes] = await Promise.all([
        statsAPI.get(),
        analyticsAPI.getSummary(parseInt(timeRange)),
        analyticsAPI.getRecentActivity(10)
      ]);
      setStats(statsRes.data);
      setAnalytics(analyticsRes.data);
      setRecentActivity(activityRes.data);
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
    }
    setLoading(false);
  }, [timeRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Calculate trend (mock for now - would need historical data)
  const getTrendIcon = (positive = true) => positive 
    ? <ArrowUpRight className="text-green-500" size={16} />
    : <ArrowDownRight className="text-red-500" size={16} />;

  // Use ?? for nullish coalescing (0 is a valid value)
  const mainStats = [
    { 
      label: 'মোট পেজ ভিউ', 
      value: analytics?.total_page_views ?? 0, 
      icon: Eye, 
      color: 'bg-blue-500',
      trend: null, // No fake trends
      positive: true
    },
    { 
      label: 'ইউনিক ভিজিটর', 
      value: analytics?.total_unique_visitors ?? 0, 
      icon: Users, 
      color: 'bg-green-500',
      trend: null,
      positive: true
    },
    { 
      label: 'মোট লিড', 
      value: analytics?.total_leads ?? stats.leads ?? 0, 
      icon: UserPlus, 
      color: 'bg-purple-500',
      trend: null,
      positive: true
    },
    { 
      label: 'অপেক্ষমাণ রিভিউ', 
      value: analytics?.pending_reviews ?? 0, 
      icon: Clock, 
      color: 'bg-yellow-500',
      trend: (analytics?.pending_reviews ?? 0) > 0 ? 'পদক্ষেপ প্রয়োজন' : 'শূন্য',
      positive: (analytics?.pending_reviews ?? 0) === 0
    },
  ];

  const contentStats = [
    { label: 'মোট গাইড', value: analytics?.total_guides ?? stats.guides ?? 0, icon: FileText, color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' },
    { label: 'মোট ব্লগ', value: analytics?.total_blogs ?? 0, icon: BarChart3, color: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' },
    { label: 'সেবাদাতা', value: analytics?.total_services ?? stats.services ?? 0, icon: Users, color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400' },
    { label: 'মোট রিভিউ', value: analytics?.total_reviews ?? stats.ratings ?? 0, icon: Star, color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400' },
    { label: 'অনুমোদিত রিভিউ', value: analytics?.approved_reviews ?? 0, icon: CheckCircle, color: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400' },
    { label: 'FAQ', value: stats.faqs ?? 0, icon: HelpCircle, color: 'bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400' },
  ];

  // Simple bar chart for daily stats
  const renderMiniChart = () => {
    const dailyStats = analytics?.daily_stats || [];
    if (dailyStats.length === 0) return null;

    const maxViews = Math.max(...dailyStats.map(d => d.page_views || 0), 1);
    const lastDays = dailyStats.slice(-14); // Last 14 days

    return (
      <div className="flex items-end gap-1 h-16">
        {lastDays.map((day, idx) => {
          const height = ((day.page_views || 0) / maxViews) * 100;
          return (
            <div
              key={idx}
              className="flex-1 bg-[#2E7D32] rounded-t-sm transition-all hover:bg-[#1B5E20]"
              style={{ height: `${Math.max(height, 5)}%` }}
              title={`${day.date}: ${day.page_views} views`}
            />
          );
        })}
      </div>
    );
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'lead': return <UserPlus size={16} className="text-purple-500" />;
      case 'review': return <Star size={16} className="text-yellow-500" />;
      default: return <Eye size={16} className="text-blue-500" />;
    }
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">ড্যাশবোর্ড</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">আপনার ওয়েবসাইটের সারসংক্ষেপ</p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-full sm:w-36" data-testid="time-range-select">
              <Calendar size={14} className="mr-1" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">গত ৭ দিন</SelectItem>
              <SelectItem value="30">গত ৩০ দিন</SelectItem>
              <SelectItem value="90">গত ৯০ দিন</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            size="icon" 
            onClick={fetchData}
            disabled={loading}
            className="shrink-0"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
      </div>

      {/* Main Stats - Mobile First Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        {mainStats.map((stat) => (
          <div 
            key={stat.label} 
            className="bg-white dark:bg-gray-800 rounded-xl p-4 md:p-5 border border-gray-200 dark:border-gray-700 hover:border-[#2E7D32] transition-colors"
          >
            <div className="flex items-start justify-between mb-3">
              <div className={`w-10 h-10 rounded-lg ${stat.color} flex items-center justify-center`}>
                <stat.icon className="text-white" size={20} />
              </div>
              {stat.trend && (
                <div className="flex items-center gap-1 text-xs">
                  {stat.positive ? getTrendIcon(true) : getTrendIcon(false)}
                  <span className={stat.positive ? 'text-green-600' : 'text-red-500'}>{stat.trend}</span>
                </div>
              )}
            </div>
            <p className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">{formatNumber(stat.value)}</p>
            <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mt-1">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Middle Section - Chart & Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Page Views Chart */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl p-4 md:p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <TrendingUp size={18} className="text-[#2E7D32]" />
              পেজ ভিউ (গত {formatTimeRange(timeRange)} দিন)
            </h3>
          </div>
          {analytics?.daily_stats?.length > 0 ? (
            <div className="space-y-4">
              {renderMiniChart()}
              <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                <span>{analytics.daily_stats[Math.max(0, analytics.daily_stats.length - 14)]?.date}</span>
                <span>{analytics.daily_stats[analytics.daily_stats.length - 1]?.date}</span>
              </div>
            </div>
          ) : (
            <div className="h-20 flex items-center justify-center text-gray-400">
              এখনো কোনো ডেটা নেই
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 md:p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Clock size={18} className="text-[#2E7D32]" />
            সাম্প্রতিক কার্যক্রম
          </h3>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {recentActivity.length > 0 ? recentActivity.map((activity, idx) => (
              <div key={idx} className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center shrink-0">
                  {getActivityIcon(activity.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{activity.title}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{activity.subtitle}</p>
                </div>
              </div>
            )) : (
              <p className="text-sm text-gray-400 text-center py-4">কোনো কার্যক্রম নেই</p>
            )}
          </div>
        </div>
      </div>

      {/* Content Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {contentStats.map((stat) => (
          <div key={stat.label} className="bg-white dark:bg-gray-800 rounded-xl p-3 md:p-4 border border-gray-200 dark:border-gray-700">
            <div className={`w-8 h-8 rounded-lg ${stat.color} flex items-center justify-center mb-2`}>
              <stat.icon size={16} />
            </div>
            <p className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">{formatNumber(stat.value)}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Top Content Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        {/* Top Guides */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 md:p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <FileText size={18} className="text-blue-500" />
            জনপ্রিয় গাইড
          </h3>
          <div className="space-y-3">
            {(analytics?.top_guides || []).slice(0, 5).map((guide, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 text-xs flex items-center justify-center font-medium">
                  {idx + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900 dark:text-white truncate">{guide.title}</p>
                  <p className="text-xs text-gray-500">{formatNumber(guide.views || 0)} ভিউ</p>
                </div>
              </div>
            ))}
            {(!analytics?.top_guides || analytics.top_guides.length === 0) && (
              <p className="text-sm text-gray-400 text-center py-2">কোনো ডেটা নেই</p>
            )}
          </div>
        </div>

        {/* Top Blogs */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 md:p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <BarChart3 size={18} className="text-green-500" />
            জনপ্রিয় ব্লগ
          </h3>
          <div className="space-y-3">
            {(analytics?.top_blogs || []).slice(0, 5).map((blog, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 text-xs flex items-center justify-center font-medium">
                  {idx + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900 dark:text-white truncate">{blog.title}</p>
                  <p className="text-xs text-gray-500">{formatNumber(blog.views || 0)} ভিউ</p>
                </div>
              </div>
            ))}
            {(!analytics?.top_blogs || analytics.top_blogs.length === 0) && (
              <p className="text-sm text-gray-400 text-center py-2">কোনো ডেটা নেই</p>
            )}
          </div>
        </div>

        {/* Top Services */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 md:p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Users size={18} className="text-purple-500" />
            জনপ্রিয় সেবাদাতা
          </h3>
          <div className="space-y-3">
            {(analytics?.top_services || []).slice(0, 5).map((service, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-600 text-xs flex items-center justify-center font-medium">
                  {idx + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900 dark:text-white truncate">{service.name}</p>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span>{service.views || 0} ভিউ</span>
                    {service.avg_rating > 0 && (
                      <span className="flex items-center gap-0.5">
                        <Star size={10} className="fill-yellow-400 text-yellow-400" />
                        {service.avg_rating}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {(!analytics?.top_services || analytics.top_services.length === 0) && (
              <p className="text-sm text-gray-400 text-center py-2">কোনো ডেটা নেই</p>
            )}
          </div>
        </div>
      </div>

      {/* Rating Overview */}
      {analytics?.avg_rating > 0 && (
        <div className="bg-gradient-to-r from-[#2E7D32] to-[#1B5E20] rounded-xl p-4 md:p-6 text-white">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                <Star size={32} className="fill-yellow-400 text-yellow-400" />
              </div>
              <div>
                <p className="text-sm opacity-80">গড় রেটিং</p>
                <p className="text-3xl font-bold">{analytics.avg_rating}</p>
              </div>
            </div>
            <div className="text-center sm:text-right">
              <p className="text-sm opacity-80">মোট অনুমোদিত রিভিউ</p>
              <p className="text-2xl font-bold">{analytics.approved_reviews}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardOverview;
